import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useApp } from '../AppContext';
import { publicApi } from '../lib/api';
import { Field } from '../components/ui';
import GoogleButton from '../components/GoogleButton';

export default function Login() {
  const { signIn, signInWithGoogle, toast } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [methods, setMethods] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // The server decides whether Google is available; asking avoids shipping a
  // button that can't work when GOOGLE_CLIENT_ID isn't set.
  useEffect(() => {
    let cancelled = false;
    publicApi.get('/auth/methods')
      .then(m => { if (!cancelled) setMethods(m); })
      .catch(() => { if (!cancelled) setMethods({ password: true, google: false }); });
    return () => { cancelled = true; };
  }, []);

  const goHome = () => navigate(location.state?.from?.pathname || '/dashboard', { replace: true });

  async function onGoogle(credential) {
    setBusy(true); setError('');
    try {
      await signInWithGoogle(credential);
      goHome();
    } catch (err) {
      // The server's message here is deliberately actionable — "ask the owner
      // to invite you" rather than a generic failure — so show it as-is.
      setError(err.message || 'Google sign-in failed');
      toast(err.message || 'Google sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  // Demo credentials are only meaningful while the demo seed is loaded; in a
  // real deployment SEED_DEMO is off and these accounts don't exist.
  const showDemo = import.meta.env.DEV;

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await signIn(email, password);
      navigate(location.state?.from?.pathname || '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Sign in failed');
      toast(err.message || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-wrap">
      <img className="logo" src="/logo.png" alt="" />
      <h1>5 River Roofing</h1>
      <p className="sb">Field-to-finish operations app for the team.</p>

      {showDemo ? (
        <div className="demo">
          <b>Demo accounts</b><br />
          Owner → <code>sukhwant@5riverroofing.com</code> / <code>5river123</code><br />
          Field Rep → <code>rep@5riverroofing.com</code> / <code>rep123</code>
        </div>
      ) : null}

      {methods?.google ? (
        <>
          <GoogleButton
            clientId={methods.googleClientId}
            onCredential={onGoogle}
            disabled={busy}
          />
          <div className="or-divider"><span>or</span></div>
        </>
      ) : null}

      <form className="col" style={{ gap: 'var(--s-3)' }} onSubmit={onSubmit}>
        <Field label="Email" id="email">
          <input
            id="email" className="fi" type="email" name="email"
            autoComplete="email" required
            value={email} onChange={e => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password" id="password" error={error}>
          <div className="password-input">
            <input
              id="password" className="fi" type={showPassword ? 'text' : 'password'} name="password"
              autoComplete="current-password" required
              value={password} onChange={e => setPassword(e.target.value)}
            />
            <button
              className="password-toggle" type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword(v => !v)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                {showPassword ? <><path d="M3 3l18 18" /><path d="M10.6 10.7a2 2 0 0 0 2.7 2.7" /><path d="M9.9 5.2A10.8 10.8 0 0 1 12 5c5.2 0 8.7 4.6 9.7 7a16.7 16.7 0 0 1-3.1 4.3M6.2 6.2C3.9 7.7 2.6 10 2.3 12c.5 1.2 1.7 3.3 4.1 5M14.1 18.7c-.7.2-1.4.3-2.1.3-5.2 0-8.7-4.6-9.7-7" /></> : <><path d="M2.3 12C3.3 9.6 6.8 5 12 5s8.7 4.6 9.7 7c-1 2.4-4.5 7-9.7 7s-8.7-4.6-9.7-7Z" /><circle cx="12" cy="12" r="2.5" /></>}
              </svg>
            </button>
          </div>
        </Field>
        <button className="btn pri" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {showDemo ? (
          <button
            className="btn" type="button"
            onClick={() => { setEmail('sukhwant@5riverroofing.com'); setPassword('5river123'); }}
          >
            Quick-fill owner
          </button>
        ) : null}
        <Link className="sb" to="/forgot" style={{ textAlign: 'center' }}>
          Forgotten your password?
        </Link>
      </form>
    </main>
  );
}
